/**
 * Generally useful utility functions. Not be specific to any resource or page.
 */

import isEqual from '@wry/equality';
import type { MutableRefObject } from 'react';
import { useEffect, useRef } from 'react';
import { head, keyBy, tail } from 'lodash';
import type { FetchResult } from '@apollo/client';
import { parseClientId } from '../components/core/ClientId';
import { MultiKey } from '../components/core/MultiKey';
import { mapKeyBy } from '@seaber/common/lib/collections';
import { isDefined } from '@seaber/common/lib/generics';
import { memoize2 } from '@seaber/common/lib/memoize';
// TODO: Remove and update imports
export {
  memoize,
  memoize2,
  memoize3,
  memoize4,
  memoize5,
} from '@seaber/common/lib/memoize';
export { mapKeyBy };

export * from '@seaber/common/lib/generics';
export * from '@seaber/common/lib/parsing';

/** Replaces the contents of *xs* with contents from *ys*. Returns *xs*.*/
export const replaceContents = <T>(xs: T[], ys: T[]): T[] => {
  xs.splice(0, xs.length, ...ys);
  return xs;
};

export type Tail<T extends unknown[]> = T extends [
  head: unknown,
  ...tail: infer Tail_,
]
  ? Tail_
  : never;

export type Initial<T extends unknown[]> = T extends [
  ...tail: infer Tail_,
  head: unknown,
]
  ? Tail_
  : never;

export type Head<T extends unknown[]> = T extends [] ? never : T[0];

export type Last<T extends unknown[]> = T extends [
  ...tail: unknown[],
  head: infer Head_,
]
  ? Head_
  : never;

/**
 * type A = [1, 2];
   const a: TuplePrefix<A> = [1]; // ok
   const b: TuplePrefix<A> = [2]; // NOT ok
   const c: TuplePrefix<A> = [1, 2]; // ok
   const e: TuplePrefix<A> = [1, 2, 3]; // NOT ok
 */
export type TuplePrefix<T extends unknown[]> = T extends []
  ? []
  : T extends [...infer Head, infer End]
    ? [...Head, End] | TuplePrefix<Head>
    : never;

export type NonEmptyArray<T> = [T, ...T[]];

export type Id<
  T extends { id?: K | undefined | null } | Record<string, unknown>,
  K extends string | number = string | number,
> = T & { id: K };

export type WithId<
  T extends Record<string, unknown>,
  Brand extends number,
> = T & {
  id: Brand;
};
export type WithNullableId<
  T extends Record<string, unknown>,
  Brand extends number,
> = T & {
  id: Brand | null;
};

export type Forbidden<T, K extends string> = T & { [K1 in K]?: undefined };

/** Validates that Target does not have any extra properties compared to Accepted.
 * Also makes sure that every optional field is specified.
 */
export type ExactProps<Accepted, Target> = {
  [K1 in keyof Accepted]-?: Accepted[K1];
} & { [K2 in Exclude<keyof Target, keyof Accepted>]?: undefined };

/** Just a helper to make the cast, can sometimes be convenient when you don't want to assign the cast result to a variable.
 * Returns the parameter with no changes.
 */
export function exactProps<Accepted, Target>(
  x: ExactProps<Accepted, Target>,
): ExactProps<Accepted, Target> {
  return x as unknown as ExactProps<Accepted, Target>;
}

/** Validates that Target does not have any extra properties compared to Accepted.
 */
export type NoExtraProps<Accepted, Target> = {
  [K1 in keyof Accepted]: Accepted[K1];
} & { [K2 in Exclude<keyof Target, keyof Accepted>]?: undefined };

/** Just a helper to make the cast, can sometimes be convenient when you don't want to assign the cast result to a variable.
 * Returns the parameter with no changes.
 */
export function noExtraProps<Accepted, Target>(
  x: NoExtraProps<Accepted, Target>,
): NoExtraProps<Accepted, Target> {
  return x as unknown as NoExtraProps<Accepted, Target>;
}

export type CastFields<
  T extends { [key: string]: unknown },
  U extends keyof T,
  V,
> = {
  [K in keyof T]: K extends U ? V : T[K];
};

export type ChangeTypes<
  T extends { [key: string]: unknown },
  U extends { [key in keyof Partial<T>]: unknown },
> = {
  [K in keyof T]: K extends keyof U ? U[K] : T[K];
};

export function hasId<
  T extends { [key: string]: unknown },
  K extends string | number = string | number,
>(x: T): x is Id<T, K> {
  return x.id !== undefined && x.id !== null;
}

/** In place of `const value = maybeObj?.maybeValue && callSomething(maybeObj?.maybeValue)`
 * You can avoid the repeat: `const value = withDefined(maybeObj?.maybeValue, callSomething)` */
export function withDefined<T, R>(
  x: T | undefined | null,
  fn: (y: T) => R,
): R | null {
  return isDefined(x) ? fn(x) : null;
}

/** Use this in place of console.log when you want to quickly debug log something
 * in an anon function without having to turn it into a block like:
 * const fn = (x: number) => logFn(x) || x * 3; */
export function logFn(...args: unknown[]): null {
  console.log(...args);
  return null;
}

/** Deals with stupid react-hook-form data which is typed to be numbers but in reality they're strings
 * or sometimes undefined when you need to output numbers.
 */
export function inputToNumber(
  x: number | string | null | undefined,
): number | undefined {
  return inputToNumberWithDefault(x, undefined);
}

export function inputToNumberWithDefault<E>(
  x: number | string | null | undefined,
  emptyValue: E,
): number | E {
  if (x) {
    return Number(x);
  } else {
    return emptyValue;
  }
}

export function setUnion<T>(sets: ReadonlySet<T>[]): Set<T> {
  return new Set(sets.flatMap((s) => [...s]));
}

export function setIntersection<T>(sets: ReadonlySet<T>[]): Set<T> {
  const [a, rest] = [head(sets), tail(sets)];
  if (!a) {
    return new Set();
  }

  const result = new Set(a);
  const aItems = a.values();

  for (const s of rest) {
    for (const x of aItems) {
      if (!s.has(x)) {
        result.delete(x);
      }
    }
  }

  return result;
}

export function setDifference<T>(
  a: ReadonlySet<T>,
  rest: ReadonlySet<T>[],
): Set<T> {
  const result = new Set(a);

  for (const s of rest) {
    for (const x of s.values()) {
      result.delete(x);
    }
  }

  return result;
}

export function setSome<T>(
  set: ReadonlySet<T>,
  pred: (x: T) => boolean,
): boolean {
  for (const x of set) {
    if (!pred(x)) {
      return false;
    }
  }
  return true;
}

type Diff<T> = {
  created: Map<number | string, T>;
  updated: Map<number | string, T>;
  deleted: Map<number | string, T>;
};

/** Constructs a Diff comparison object from two arrays of same type, keyed by the given id field */
export const diffBy = <T, Id extends keyof T>(
  original: T[],
  changed: T[],
  idField: Id,
): Diff<T> => {
  const keyedOriginal = keyBy(original, idField);
  const keyedChanged = keyBy(changed, idField);

  const created = new Map<number | string, T>();
  const updated = new Map<number | string, T>();
  const deleted = new Map<number | string, T>();

  for (const id of Object.keys(keyedOriginal)) {
    const a = keyedOriginal[id];
    const b = keyedChanged[id];
    if (!b) {
      deleted.set(id, a);
    } else if (!isEqual(a, b)) {
      updated.set(id, b);
    }
  }

  for (const id of Object.keys(keyedChanged)) {
    const a = keyedOriginal[id];
    const b = keyedChanged[id];
    if (!a) {
      created.set(id, b);
    }
  }

  return { created, updated, deleted };
};

const humanReadableIdsByClientId = new Map<string, string>();
// Cleans some values in an object recursively for easier to interpret debug output:
// - Turns proxy type objects into readable ones
// - Adds conversions to date for timestamps
// - Makes clientIds easier to read and match by eye
export function dbgVal<T extends unknown | Record<string, unknown>>(
  o: T,
  seen: WeakMap<object, unknown> = new WeakMap(),
): unknown {
  // noinspection SuspiciousTypeOfGuard
  if (
    (typeof o === 'number' && Math.round(o).toString().length === 13) ||
    o instanceof Date
  ) {
    return new Date(o);
  } else if (typeof o !== 'object' || o === null) {
    return o;
  } else if (o && 'precedence' in o && 'toString' in o && 'valueOf' in o) {
    return `${o.toString()}: ${o.valueOf()}`;
  }

  // Check for circular reference
  const existing = seen.get(o);
  if (existing) {
    return existing;
  }

  const res: Record<string, unknown> = {};
  seen.set(o, res); // Mark this object as seen

  // noinspection SuspiciousTypeOfGuard
  const entries =
    o instanceof Map
      ? o.entries()
      : o instanceof Set
        ? o.entries()
        : o instanceof MultiKey
          ? Object.entries(o.toJS())
          : // eslint-disable-next-line @typescript-eslint/no-explicit-any
            Object.entries({ ...(o as any) });

  for (const [k, v] of entries) {
    if (typeof v === 'number' && Math.round(v).toString().length === 13) {
      res[k] = v;
      res[`${k}AsDate`] = new Date(v).toISOString();
    } else if (typeof v === 'string' && parseClientId(v).length === 32) {
      const clientId = parseClientId(v);
      let cleanClientId = humanReadableIdsByClientId.get(clientId);
      if (!cleanClientId) {
        cleanClientId = `C-${humanReadableIdsByClientId.size}`;
        humanReadableIdsByClientId.set(clientId, cleanClientId);
      }
      res[k] = v;
      res[`${k}AsClean`] = cleanClientId;
    } else if (Array.isArray(v)) {
      res[k] = v.map((item) => dbgVal(item, seen));
    } else if (v instanceof Map) {
      res[k] = Object.fromEntries(
        Array.from(v).map(([k, v]) => [k, dbgVal(v, seen)]),
      );
    } else if (v && typeof v === 'object') {
      res[k] = dbgVal(v, seen);
    } else {
      res[k] = v;
    }
  }
  return res;
}

let dbgLoggingEnabled = true;
export function toggleDbgLogging(isOn: boolean) {
  dbgLoggingEnabled = isOn;
}
// Console debug but transform all params
export function dbg(...args: unknown[]): null {
  if (!dbgLoggingEnabled) {
    return null;
  }
  console.log(...args.map((arg) => dbgVal(arg)));
  return null;
}

export function useWhyDidYouUpdate<T extends { [key: string]: unknown }>(
  props: T,
  name?: string,
): void {
  const previousProps = useRef<T>();

  useEffect(() => {
    if (previousProps.current) {
      const allKeys: Array<keyof T> = Object.keys({
        ...previousProps.current,
        ...props,
      });
      const changesObj: Partial<{ [K in keyof T]: { from: T[K]; to: T[K] } }> =
        {};

      allKeys.forEach((key) => {
        if (
          previousProps.current &&
          previousProps.current[key] !== props[key]
        ) {
          changesObj[key] = {
            from: previousProps.current[key],
            to: props[key],
          };
        }
      });

      if (Object.keys(changesObj).length) {
        console.log('[why-did-you-update]', name, changesObj);
      }
    }

    previousProps.current = props;
  });
}

export function useDidChange<T>(name: string, value: T): void {
  const v = useRef<T>(value);
  console.log(
    'DidChange:',
    name,
    value !== v.current,
    ...(value !== v.current ? [v.current, '==>', value] : []),
  );
  v.current = value;
}

export function useDeepMemo<T>(value: T): T {
  const ref = useRef<T>(value);

  if (value === ref.current) {
    return ref.current;
  }

  if (!isEqual(value, ref.current)) {
    ref.current = value;
  }

  return ref.current;
}

/** Used on arrays, memoizes each array item independently. When an item is deeply equal with its previous version, reference to the previous version object is returned. */
export function useItemwiseDeepMemo<T, K>(value: T[], keyBy: (x: T) => K): T[] {
  const ref = useRef<T[]>(value);
  const prevMapped = mapKeyBy(ref.current, keyBy);

  let numEqual = 0;
  const itemwiseResult = value.map((x, i) => {
    const prev = prevMapped.get(keyBy(x));
    if (prev && isEqual(prev, x)) {
      // Identical item, use the same reference
      if (ref.current[i] === prev) {
        // If also at the same position, mark as fully equal
        ++numEqual;
      }
      return prev;
    } else {
      return x;
    }
  });

  // Can use the same array reference if every item is identical and the order of items is also the same.
  const allEqual =
    value.length === ref.current.length && numEqual === value.length;

  if (!allEqual) {
    ref.current = itemwiseResult;
  }

  return ref.current;
}

export function useAsRef<T>(x: T): MutableRefObject<T> {
  const ref = useRef<T>(x);
  ref.current = x;
  return ref;
}

/** Like Array.find but instead of the first match, returns the nth one. Using n=1 is equal to Array.find */
export const findNth = <T>(
  xs: T[],
  n: number,
  p: (x: T) => boolean,
): T | undefined => {
  let numMatches = 0;
  for (const x of xs) {
    if (p(x)) {
      numMatches++;
      if (numMatches === n) {
        return x;
      }
    }
  }
  return undefined;
};

/** Returns the element in `xs` with the highest value evaluted by `fn` */
export const findBestIndexWith = <T>(
  xs: T[],
  fn: (x: T) => number,
): number | undefined => {
  const bestValue = -Infinity;
  let result: number | undefined = undefined;
  for (let i = 0; i < xs.length; ++i) {
    const x = xs[i];
    const value = fn(x);
    if (value > bestValue) {
      result = i;
    }
  }
  return result;
};

/** Finds a value in a set or a map. Returns the value or undefined if not found. */
export const setFind = <T>(
  xs: Set<T> | Map<unknown, T>,
  p: (x: T) => boolean,
): T | undefined => {
  for (const x of xs.values()) {
    if (p(x)) {
      return x;
    }
  }
  return undefined;
};

/** Filters the values in a set and returns a new set.*/
export const setFilter = <T>(xs: Set<T>, p: (x: T) => boolean): Set<T> => {
  const result = new Set<T>();
  for (const x of xs.values()) {
    if (p(x)) {
      result.add(x);
    }
  }
  return result;
};

/** Filters the values in a map and returns a new map.*/
export const mapFilter = <K, V>(
  xs: Map<K, V>,
  p: (v: V, k: K) => boolean,
): Map<K, V> => {
  const result = new Map<K, V>();
  for (const [k, v] of xs.entries()) {
    if (p(v, k)) {
      result.set(k, v);
    }
  }
  return result;
};

export const mapFind = <K, V>(
  xs: Map<K, V>,
  p: (v: V, k: K) => boolean,
): [K, V] | undefined => {
  for (const [k, v] of xs) {
    if (p(v, k)) {
      return [k, v];
    }
  }
  return undefined;
};

export type RangeValueType = number | Date | undefined;

export type Range<T extends RangeValueType = number> = {
  start: T;
  end: T;
  startExclusive?: boolean;
  endExclusive?: boolean;
};

/** Same as arr.map(mapper).find((x) => x !== null), but we avoid mapping every element and constructing the result array. */
export function mappedFind<T, U>(
  arr: Iterable<T>,
  mapper: (x: T) => U | null,
): U | null {
  for (const x of arr) {
    const mapped = mapper(x);
    if (mapped !== null) {
      return mapped;
    }
  }
  return null;
}

/** Same as Math.min(...arr.map(mapper)), but more efficient as we avoid constructing an extra result array. */
export function mappedMin<T>(
  arr: Iterable<T>,
  mapper: (x: T) => number,
): number {
  let min = Infinity;
  for (const x of arr) {
    const mapped = mapper(x);
    if (mapped < min) {
      min = mapped;
    }
  }
  return min;
}

/** Same as Math.max(...arr.map(mapper)), but more efficient as we avoid constructing an extra result array. */
export function mappedMax<T>(
  arr: Iterable<T>,
  mapper: (x: T) => number,
): number {
  let max = -Infinity;
  for (const x of arr) {
    const mapped = mapper(x);
    if (mapped > max) {
      max = mapped;
    }
  }
  return max;
}

export function mappedMean<T>(
  arr: Iterable<T>,
  mapper: (x: T) => number,
): number {
  let sum = 0;
  let count = 0;
  for (const x of arr) {
    const mapped = mapper(x);
    sum += mapped;
    count++;
  }
  if (count === 0) return 0;
  return sum / count;
}

export type NONETYPE = symbol & { _brand: 'nonetype' }; // The brand ensures that TS doesn't let just any symbol be assigned in place of NONE
export const NONE: NONETYPE = Symbol('NONE') as NONETYPE;
export const isNoneType = (x: unknown): x is NONETYPE => x === NONE;
export const isNotNoneType = <T>(x: T | NONETYPE): x is T => x !== NONE;

/** Performs filter and map in one operation without excessive array allocations.
 * Like xs.flatMap((x) => x ? [x] : []) but doesn't deal with arrays so is much faster and reads a bit better.
 * Note: using NONE instead of e.g. null because sometimes null might be a valid value in the result array.
 * step can be used to iterate the array in reverse (or to skip elements) */
export function filteredMap<T, U>(
  xs: readonly T[],
  fn: (x: T, i: number) => U | NONETYPE,
  step = 1,
): U[] {
  if (step === 0) {
    return [];
  }
  const result: U[] = [];
  const isReverse = step < 0;
  for (
    let i = isReverse ? xs.length - 1 : 0;
    isReverse ? i >= 0 : i < xs.length;
    i += step
  ) {
    const x = xs[i];
    const mapped = fn(x, i);
    if (mapped !== NONE) {
      result.push(mapped as U);
    }
  }
  return result;
}

export const findIndexWith = <T>(
  arr: readonly T[],
  startIndex: number,
  direction: -1 | 1,
  p: (x: T, i: number) => boolean,
  endIndex?: number,
): number | undefined => {
  let result: number | undefined = undefined;
  endIndex ??= direction === 1 ? arr.length - 1 : 0;

  for (
    let i = startIndex;
    direction === 1 ? i <= endIndex : i >= endIndex;
    i += direction
  ) {
    if (p(arr[i], i)) {
      result = i;
      break;
    }
  }

  return result;
};

export const findWith = <T>(
  arr: readonly T[],
  startIndex: number,
  direction: -1 | 1,
  p: (x: T, i: number) => boolean,
  endIndex?: number,
): T | undefined => {
  const index = findIndexWith(arr, startIndex, direction, p, endIndex);
  return index === undefined ? undefined : arr[index];
};

export const findNextIndexWith = <T>(
  arr: readonly T[],
  startIndex: number,
  p: (x: T) => boolean,
): number | undefined => {
  return findIndexWith(arr, startIndex, 1, p);
};

export const findPrevIndexWith = <T>(
  arr: readonly T[],
  startIndex: number,
  p: (x: T) => boolean,
): number | undefined => {
  return findIndexWith(arr, startIndex, -1, p);
};

/**
 * Finds the first index of occurence for each unique value in `arr` as returned by `p`.
 * When `reverse` = true, finds the last occurence instead of the first.
 *
 * Example:
 * findFirstIndexEach(['a','b','c','a','b','c'], (x) => x) returns a map of { 'a': 0, 'b': 1, 'c': 2 }
 */
export const findFirstIndexEach = <T, V>(
  arr: T[],
  p: (x: T) => V,
  reverse = false,
): Map<V, number> => {
  const result: Map<V, number> = new Map();

  for (let i = 0, x = arr[i]; i < arr.length; ++i) {
    const v = p(x);
    if (v !== undefined && (reverse || !result.has(v))) {
      result.set(v, i);
    }
  }

  return result;
};

/** Returns all valid permutations for the input array.
 * The algorithm generates new permutations from left to right and the optional `filterHead`
 * is evaluated on each new head allowing us to skip generating any invalid permutations for the rest of the array
 * when we know that for example the 1 or 2-length initial array is already invalid.
 *
 * e.g. getPermutations("2019", (x) => x !== "0") to generate all valid numbers and drop any variations which
 * would start with a "0", and internally we skip the work of generating sequences like "02", "021", "0219"
 */
export const getPermutations = <T>(
  arr: readonly T[],
  filterHead?: (head: readonly T[]) => boolean,
): T[][] => {
  const permuteTo = (head: T[], rest: readonly T[]): T[][] => {
    if (rest.length === 0) {
      return [head];
    }

    const base = [...rest];

    return base.flatMap((_x, i) => {
      const tmp = base[0];
      base[0] = base[i];
      base[i] = tmp;

      const newHead = [...head, base[0]];

      if (filterHead && !filterHead(newHead)) {
        // Early break all the sub permutations of the tail when the head is invalid
        return [];
      }

      return permuteTo(newHead, base.slice(1));
    });
  };

  return permuteTo([], arr);
};

export enum InterleaveItemResult {
  LEFT,
  RIGHT,
}

/**
 * @param a item from the first array
 * @param b item from the second array
 * @param prev the last item in the current result array
 */
export type InterleaveComparator<A, B> = (
  a: A,
  b: B,
  prev: A | B | undefined,
) => InterleaveItemResult;

/**
 * Merges two arrays into one by choosing one element at a time from either input array determined by the comparator function.
 */
export const interleave = <A, B>(
  xs: A[],
  ys: B[],
  cmp: InterleaveComparator<A, B>,
): (A | B)[] => {
  const result: (A | B)[] = [];
  let xi = 0,
    yi = 0;

  // Empty both arrays into result
  while (xi < xs.length || yi < ys.length) {
    const x = xs[xi],
      y = ys[yi];
    const selection =
      xi >= xs.length
        ? InterleaveItemResult.RIGHT
        : yi >= ys.length
          ? InterleaveItemResult.LEFT
          : cmp(x, y, result[result.length - 1]);
    if (selection === InterleaveItemResult.LEFT) {
      result.push(x);
      ++xi;
    } else {
      result.push(y);
      ++yi;
    }
  }

  return result;
};

/** Divide totalDuration by count and optionally if there is a remainder we will add it to the result.
 * To be used when we want to divide an exact duration equally between events, and we'll add the left over to (e.g.) the last event.
 * Only works correctly for non-negative integers. */
export const divideWithRemainder = (
  totalDuration: number,
  count: number,
  includeRemainder: boolean,
  minimumResultValue = 0,
): number => {
  const floor = Math.max(Math.floor(totalDuration / count), minimumResultValue);
  const floorTotal = floor * count;
  const excess = Math.max(totalDuration - floorTotal, 0);
  return includeRemainder ? floor + excess : floor;
};

export const isInRange = <T extends RangeValueType>(
  x: T,
  range: Range<T>,
  valueExclusive?: boolean,
): boolean =>
  x === undefined
    ? false
    : (range.startExclusive || valueExclusive
        ? (range.start ?? -Infinity) < x
        : (range.start ?? -Infinity) <= x) &&
      (range.endExclusive || valueExclusive
        ? (range.end ?? Infinity) > x
        : (range.end ?? Infinity) >= x);

export const rangesOverlap = <T extends RangeValueType>(
  range1: Range<T>,
  range2: Range<T>,
): boolean => {
  return (
    isInRange(range1.start, range2, range1.startExclusive) ||
    isInRange(range1.end, range2, range1.endExclusive) ||
    isInRange(range2.start, range1, range2.startExclusive) ||
    isInRange(range2.end, range1, range2.endExclusive)
  );
};

export const rangeUnion = <T extends RangeValueType>(
  range1: Range<T>,
  range2: Range<T>,
): Range<T> => {
  const startNum = Math.min(
    +(range1.start ?? -Infinity),
    +(range2.start ?? -Infinity),
  );
  const endNum = Math.max(+(range1.end ?? Infinity), +(range2.end ?? Infinity));
  return {
    start: (range1.start instanceof Date || range2.start instanceof Date
      ? new Date(startNum)
      : startNum) as T,
    end: (range1.end instanceof Date || range2.end instanceof Date
      ? new Date(endNum)
      : endNum) as T,
    startExclusive:
      range1.start === range2.start
        ? range1.startExclusive && range2.startExclusive
        : (range1.start ?? -Infinity) < (range2.start ?? -Infinity)
          ? range1.startExclusive
          : range2.startExclusive,
    endExclusive:
      range1.end === range2.end
        ? range1.endExclusive && range2.endExclusive
        : (range1.end ?? Infinity) > (range2.end ?? Infinity)
          ? range1.endExclusive
          : range2.endExclusive,
  };
};

export type DeepReadOnly<T> =
  T extends Map<infer K, infer V>
    ? ReadonlyMap<K, DeepReadOnly<V>>
    : T extends Set<infer V>
      ? ReadonlySet<DeepReadOnly<V>>
      : T extends Array<infer V>
        ? ReadonlyArray<DeepReadOnly<V>>
        : T extends { [key: string]: unknown }
          ? {
              readonly [P in keyof T]: DeepReadOnly<T[P]>;
            }
          : T;

export type SortedArray<T> = Array<T> & { isSorted: true };

export const sortBy = <T>(arr: T[], fn: (x: T) => number): SortedArray<T> => {
  const sorted = arr.slice().sort((a, b) => fn(a) - fn(b));
  (sorted as SortedArray<T>).isSorted = true;
  return sorted as SortedArray<T>;
};

export const enumerate = <T>(items: T[]): Array<[number, T]> =>
  items.map((item, i) => [i, item]);

export const typedKeyBy = <KeyType extends string | number, ValueType>(
  items: ValueType[],
  pred: (item: ValueType) => KeyType,
): { [K in KeyType]?: ValueType } => {
  const resDict = keyBy(items, pred);
  return resDict as { [K in KeyType]?: ValueType };
};

export const fetchResultErrorIsOK =
  <T>(errorMsgMatcher: string, warnMsg: string) =>
  (result: FetchResult<T>): FetchResult<T> => {
    if (!result.errors?.length) {
      return result;
    } else if (
      result.errors.every((err) => err.message.includes(errorMsgMatcher))
    ) {
      console.warn(warnMsg);
      result.errors = undefined;
      return result;
    } else {
      throw new Error(
        `${warnMsg}: ${result.errors.map((err) => err.message).join('; ')}`,
      );
    }
  };

export const missingOnDeleteIsOK = (msg: string) => {
  return fetchResultErrorIsOK(
    'no values you can delete were found',
    `Could not delete items because they did not exist: ${msg}`,
  );
};

/**
 * Helper for array indexing that makes it explicit that the indexed element may be undefined which is not the case with the indexing operator
 */
export const get = <T>(arr: readonly T[], idx: number): T | undefined =>
  arr[idx];

export const throttledProgressHandler = (
  stateUpdater: (remaining: number) => void,
  minTimeBetweenUpdates = 100,
) => {
  let done = false;
  let frame: number | undefined;
  let lastUpdated = Date.now() - minTimeBetweenUpdates;
  return (remaining: number) => {
    if (!remaining) {
      stateUpdater(remaining);
      done = true;
      return;
    }
    const now = Date.now();
    const diff = now - lastUpdated;
    if (diff > minTimeBetweenUpdates && !frame) {
      frame = requestAnimationFrame(() => {
        if (!done) {
          stateUpdater(remaining);
          frame = undefined;
        }
      });
      lastUpdated = now;
    }
  };
};

/** Groups/splits items by a property so that same consecutive values belong in the same group but same values separated by a different one do not.
 * This is useful in particular for detecting unique port calls by location id
 */
export const groupBySequence = <T>(
  items: readonly T[],
  getIdentifier: (x: T, i: number) => unknown,
): T[][] => {
  let prevIdentifier: unknown = null;

  return sliceBy(items, (item, i) => {
    const currentIdentifier = getIdentifier(item, i);
    if (currentIdentifier !== prevIdentifier) {
      prevIdentifier = currentIdentifier;
      return true;
    }
    return false;
  });
};

/** Slices an array at all indices where the predicate returns true
 * This is useful in particular for detecting empty to empty voyages
 */
export const sliceBy = <T>(
  items: readonly T[],
  fn: (x: T, i: number) => boolean,
): T[][] => {
  const result: T[][] = [];
  for (let i = 0; i < items.length; ++i) {
    const item = items[i];
    if (fn(item, i) || i === 0) {
      result.push([]);
    }
    result[result.length - 1].push(item);
  }
  return result;
};

/** Like string.split but for arrays */
export const splitArray = <T>(arr: T[], splitFn: (x: T) => boolean): T[][] => {
  const result: T[][] = [];
  let current: T[] = [];
  for (const x of arr) {
    if (splitFn(x)) {
      result.push(current);
      current = [];
    } else {
      current.push(x);
    }
  }
  result.push(current);
  return result;
};

export function objectKeys<T extends object>(x: T): Array<keyof T> {
  return Object.keys(x) as (keyof T)[];
}

export function mapDropNulls<K, V>(m: Map<K, V | null>): Map<K, V> {
  return new Map(
    [...m].flatMap(([key, value]) => (value === null ? [] : [[key, value]])),
  );
}

/** flatmap on Map.values() */
export function mapFlatmap<K, V, Y>(m: Map<K, V>, fn: (v: V) => Y[]): Y[] {
  const result: Y[] = [];
  m.forEach((v) => result.push(...fn(v)));
  return result;
}

/** map on Map.values() */
export function mapMap<K, V, Y>(m: Map<K, V>, fn: (v: V, k: K) => Y): Y[] {
  const result: Y[] = [];
  m.forEach((v, k) => result.push(fn(v, k)));
  return result;
}

/** Inserts entries from m2 into m1, returns new Map. If both have the same key, uses the one from m2. */
export function mapMerge<K, V>(m1: Map<K, V>, m2: Map<K, V>): Map<K, V> {
  return new Map([...m1.entries(), ...m2.entries()]);
}

/** Maps each value in the map to another and returns a map with the same keys */
export function mapTransform<Key, V1, V2>(
  m: Map<Key, V1>,
  fn: (v: V1, k: Key) => V2,
): Map<Key, V2> {
  return new Map([...m.entries()].map(([k, v]) => [k, fn(v, k)]));
}

/** Maps each value in the record to another and returns a record with the same keys */
export function transformRecord<Key extends string | number, V1, V2>(
  r: Record<Key, V1>,
  fn: (v: V1, k: Key) => V2,
): Record<Key, V2> {
  return Object.fromEntries(
    Object.entries(r).map(([k, v]) => [k, fn(v as V1, k as Key)]),
  ) as Record<Key, V2>;
}

export function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/** Returns the number of elements matching the condition */
export function countWith<T>(xs: T[], fn: (x: T) => boolean): number {
  let result = 0;
  for (const x of xs) {
    if (fn(x)) {
      result++;
    }
  }
  return result;
}

/** Like lodash takeWhile, except can also define a starting index */
export function takeWhileFrom<T>(
  xs: T[],
  from: number,
  p: (x: T) => boolean,
): T[] {
  const result: T[] = [];
  for (let i = from; i < xs.length && p(xs[i]); ++i) {
    result.push(xs[i]);
  }
  return result;
}

/** Takes two sub arrays from xs and swaps their places. *a* and *b* are ranges of indices, end index exclusive.
 * Ranges *a* and *b* are not allowed to overlap.
 * Example: xs = ['a', 'B', 'c', 'D', 'E', 'f'], swapSubArrays(xs, [1, 2], [3, 5]) => ['a', 'D', 'E', 'c', 'B', 'f']
 */
export function swapSubArrays<T>(
  xs: T[],
  a0: readonly [start: number, end: number],
  b0: readonly [start: number, end: number],
): T[] {
  const result = xs.slice();

  // Reassign so that *a* range comes first *b* to simplify logic later
  const [a, b] = a0[0] < b0[0] ? [a0, b0] : [b0, a0];

  const [aStart, aEnd] = a;
  const [bStart, bEnd] = b;
  if (aStart > aEnd || bStart > bEnd) {
    throw new Error('ranges should be ordered');
  }
  if (aEnd > bStart) {
    throw new Error('ranges cannot overlap');
  }

  const aArr = result.slice(aStart, aEnd);
  const bArr = result.splice(bStart, bEnd - bStart, ...aArr);
  result.splice(aStart, aEnd - aStart, ...bArr);

  return result;
}

/** Take an element from an array and place it at another index */
export const reposition = <T>(
  arr: T[],
  from: number,
  count: number,
  to: number,
): T[] => {
  const res = [...arr];
  count = Math.max(count, arr.length - from);
  const elems = res.splice(from, count);
  res.splice(to, 0, ...elems);
  return res;
};

/** Roll an integer between 0 and x exclusive */
export const roll = (x: number): number => Math.floor(Math.random() * x);

/**
 * Rounds a number or string to the specified precision.
 *
 * @param value - The number or string to round.
 * @param precision - The number of decimal places to round to.
 * @returns The rounded number.
 */
export function roundTo(value: number | string, precision: number): number {
  const multiplier = Math.pow(10, precision);
  return Math.round(Number(value) * multiplier) / multiplier;
}

/** Rounds a number to the nearest multiple of a given bucket size */
export function roundToBucket(
  value: number,
  bucket: number,
  method: 'round' | 'floor' | 'ceil' = 'round',
): number {
  return Math[method](value / bucket) * bucket;
}

/**
 * Formats hours into a readable string with days and/or hours.
 * If >= 24 hours and showHours is true, returns days and hours like "2.5 days (60 h)"
 * If >= 24 hours and showHours is false, returns only days like "2.5 days"
 * If < 24 hours, returns only hours like "18 h"
 * @param totalHours Number of hours
 * @param showHours If true, shows both days and hours when >= 24h
 */
export function formatHToDays(totalHours: number, showHours: boolean): string {
  const totalDays = totalHours / 24;

  const formattedDays = totalDays.toFixed(1);

  const formattedHours = totalHours.toFixed(0);

  if (totalDays >= 1) {
    if (showHours) {
      return `${formattedDays} days (${formattedHours} h)`;
    } else return `${formattedDays} days`;
  } else {
    return `${formattedHours} h`;
  }
}

/**
 * Given the number of hours, returns the number of days and hours like "2.5 days (60 h)"
 * @param totalHours
 */
export function formatHToDaysHour(totalHours: number): string {
  return formatHToDays(totalHours, true);
}

/**
 * Given a number of days, returns a readable string
 * If the value is an integer, returns "2 days"
 * If the value has decimals, returns it truncated to 1 decimal place (e.g. "2.8 days")
 * @param totalDays number of days
 */
export function formatDays(totalDays: number): string {
  if (Number.isInteger(totalDays)) {
    return `${totalDays} days`;
  } else {
    return `${Math.trunc(totalDays * 10) / 10} days`;
  }
}

/** Takes two type guard and returns a new one which matches either of the original ones */
export const composeTypeGuardOr =
  <T1, T2>(guardA: (x: unknown) => x is T1, guardB: (x: unknown) => x is T2) =>
  (x: T1 | T2): x is T1 | T2 =>
    guardA(x) || guardB(x);

/** Takes a type guard and returns a new type guard which matches all other types except the one checked by the parameter guard.
 * For example: guardNot(isUserScenario) => matches all other scenario types except UserScenario.
 */
export const guardNot =
  <T, U>(guard: (x: T | U) => x is T) =>
  (x: T | U): x is Exclude<T | U, T> =>
    !guard(x);

export function split<T>(
  arr: readonly T[],
  predicate: (el: T) => boolean,
): { yes: T[]; no: T[] } {
  const yes = [];
  const no = [];
  for (const el of arr) {
    if (predicate(el)) {
      yes.push(el);
    } else {
      no.push(el);
    }
  }
  return { yes, no };
}
export const splitM = memoize2(split);

export function mapToSet<Element, Value>(
  iterable: Iterable<Element>,
  map: (element: Element) => Value,
) {
  const set = new Set<Value>();
  for (const e of iterable) {
    set.add(map(e));
  }
  return set;
}

export class CustomPortCostImportError extends Error {
  constructor(
    message: string,
    portName: string,
    portType: string,
    row: number,
    column: string,
  ) {
    const customMessage = `${message} from port-cost: ${portName} - ${portType} at row ${row}, ${column}`;
    super(customMessage);
  }
}

// removes all spaces from text to prepare it for search matching
export const normalizeForSearch = (text: string | null | undefined): string => {
  return text?.replaceAll(' ', '') ?? '';
};

export function concatIterablesToSet<T>(a: Iterable<T>, b: Iterable<T>) {
  const set = new Set<T>();
  for (const o of a) {
    set.add(o);
  }
  for (const o of b) {
    set.add(o);
  }
  return set;
}
